/**
 * ─── Pawnshop Entity Definitions ────────────────────────────
 * Self-registers all Pawnshop entities with the Entity Registry.
 *
 * Aggregate Root: PawnTicket
 * Entities: PawnCustomer, PawnItem, PawnPayment, AuctionItem
 */

import { EntityRegistry } from '@repo/core'
import type { EntityDefinition } from '@repo/core'
import type {
  PawnCustomer, PawnItem, PawnTicket, PawnPayment, AuctionItem,
} from './pawnshop.schema'
import { PawnshopHooks } from './pawnshop.hooks'

export const PawnCustomerEntity: EntityDefinition<PawnCustomer> = {
  name: 'pawnshop_customers',
  ui: {
    label: 'Customer', labelPlural: 'Customers', icon: 'Users',
    routePath: 'pawnshop/customers', color: 'blue',
    showInNav: true, navOrder: 10, navGroup: 'Pawnshop',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'pawnshop_customer' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

export const PawnItemEntity: EntityDefinition<PawnItem> = {
  name: 'pawnshop_items',
  ui: {
    label: 'Item', labelPlural: 'Items', icon: 'Gem',
    routePath: 'pawnshop/items', color: 'purple',
    showInNav: true, navOrder: 20, navGroup: 'Pawnshop',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'pawnshop_item' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

export const PawnTicketEntity: EntityDefinition<PawnTicket> = {
  name: 'pawnshop_tickets',
  ui: {
    label: 'Ticket', labelPlural: 'Tickets', icon: 'FileText',
    routePath: 'pawnshop/tickets', color: 'green',
    showInNav: true, navOrder: 30, navGroup: 'Pawnshop',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'pawnshop_ticket' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: false },
}

export const PawnPaymentEntity: EntityDefinition<PawnPayment> = {
  name: 'pawnshop_payments',
  ui: {
    label: 'Payment', labelPlural: 'Payments', icon: 'DollarSign',
    routePath: 'pawnshop/payments', color: 'yellow',
    showInNav: true, navOrder: 40, navGroup: 'Pawnshop',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'pawnshop_payment' },
  hooks: {},
  pagination: 'cursor',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: false },
}

export const AuctionItemEntity: EntityDefinition<AuctionItem> = {
  name: 'pawnshop_auctions',
  ui: {
    label: 'Auction', labelPlural: 'Auctions', icon: 'Gavel',
    routePath: 'pawnshop/auctions', color: 'red',
    showInNav: true, navOrder: 50, navGroup: 'Pawnshop',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'background' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'pawnshop_auction' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Self-Register ─────────────────────────────────────────

EntityRegistry.register(PawnCustomerEntity)
EntityRegistry.register(PawnItemEntity)
EntityRegistry.register(PawnTicketEntity)
EntityRegistry.register(PawnPaymentEntity)
EntityRegistry.register(AuctionItemEntity)
